/**
 * useOrgCurrency — THE way to know which currency a displayed amount is in.
 *
 * Rule of the project (docs/implementations/group/GROUP_REPORTING_CONTRACT.md):
 *   1. A row belongs to an organization; its amounts are in `organizations.currency`.
 *   2. A consolidated total over several subsidiaries is shown in the reference
 *      currency (EUR) and must be CONVERTED, never summed raw.
 *   3. No rate is ever invented. If a rate is missing, the total is not rendered as
 *      a single number — the per-currency breakdown is shown instead.
 *
 * There is ONE money formatter (`utils/formatMoney.js`) and ONE currency resolver
 * (this hook). Never a local formatter, never a per-screen helper.
 *
 *   const { money, moneyScope, sumScope, scopeCurrency, isMultiCurrency } = useOrgCurrency();
 *
 *   money(claim.estimated_amount, claim)        // → "1 234,50 zł" for a PL claim
 *   moneyScope(total)                           // → total in the scope currency
 *   const t = sumScope(claims, c => c.estimated_amount);
 *   t.complete ? moneyScope(t.value) : t.label  // label = "1 200 € + 3 400 zł"
 */

import { useMemo, useCallback } from 'react';
import { useQuery } from '@tanstack/react-query';
import { useAuth } from '../v3/context/AuthContext.jsx';
import { useOrganizations } from './useDataHooks.js';
import { formatMoney, currencySymbol } from '../utils/formatMoney.js';
import { apiRequest } from '../api/core.js';

export const REFERENCE_CURRENCY = 'EUR';

/** EUR → X rates from the `fx_rates` table. Never fabricated: absent = absent. */
function useFxRates() {
  const { data } = useQuery({
    queryKey: ['fx_rates'],
    queryFn: async () => {
      const res = await apiRequest('/fx_rate?_limit=1000');
      const rows = res?.data || [];
      const table = { [REFERENCE_CURRENCY]: 1 };
      for (const r of rows) {
        const base = String(r.base_currency || '').toUpperCase();
        const quote = String(r.quote_currency || '').toUpperCase();
        const rate = Number(r.rate);
        if (!Number.isFinite(rate) || rate <= 0) continue;
        if (base === REFERENCE_CURRENCY) table[quote] = rate;
        else if (quote === REFERENCE_CURRENCY) table[base] = 1 / rate;
      }
      return table;
    },
    staleTime: 30 * 60 * 1000,
    gcTime: 60 * 60 * 1000,
    retry: false,
  });
  return data || { [REFERENCE_CURRENCY]: 1 };
}

export function useOrgCurrency() {
  const { activeOrg, accessibleOrgs } = useAuth();
  const { data: organizations = [] } = useOrganizations();
  const rates = useFxRates();

  const currencyByOrg = useMemo(() => {
    const map = {};
    for (const o of organizations) {
      const code = o?.code || o?.org_id;
      if (code) map[String(code)] = String(o.currency || REFERENCE_CURRENCY).toUpperCase();
    }
    return map;
  }, [organizations]);

  /**
   * Currency of one row (or of an org code). The organization wins — a row's own
   * `currency` column is only a fallback when the org cannot be resolved (contract
   * invariant 1: the amounts of an org are held in that org's currency).
   * Returns null when nothing is known, so callers fall back to the scope currency.
   */
  const currencyOf = useCallback((rowOrCode) => {
    if (!rowOrCode) return null;
    if (typeof rowOrCode === 'string') return currencyByOrg[rowOrCode] || null;
    const code = rowOrCode.org_id || rowOrCode.organization_id || rowOrCode.org_code || null;
    if (code && currencyByOrg[String(code)]) return currencyByOrg[String(code)];
    return rowOrCode.currency ? String(rowOrCode.currency).toUpperCase() : null;
  }, [currencyByOrg]);

  // Currencies actually present in the current scope.
  const scopeOrgs = useMemo(() => {
    if (activeOrg) return [activeOrg];
    if (accessibleOrgs?.length) return accessibleOrgs;
    return Object.keys(currencyByOrg);
  }, [activeOrg, accessibleOrgs, currencyByOrg]);

  const scopeCurrencies = useMemo(() => {
    const set = new Set();
    for (const c of scopeOrgs) if (currencyByOrg[String(c)]) set.add(currencyByOrg[String(c)]);
    return [...set];
  }, [scopeOrgs, currencyByOrg]);

  const isMultiCurrency = scopeCurrencies.length > 1;

  /** Currency a total of the current scope is expressed in. */
  const scopeCurrency = scopeCurrencies.length === 1 ? scopeCurrencies[0] : REFERENCE_CURRENCY;

  /** Converts to the scope currency. Returns null when a rate is missing — never guesses. */
  const toScope = useCallback((amount, from) => {
    const n = Number(amount);
    if (!Number.isFinite(n)) return 0;
    const src = String(from || scopeCurrency).toUpperCase();
    if (src === scopeCurrency) return n;
    const rFrom = rates[src];
    const rTo = rates[scopeCurrency];
    if (!rFrom || !rTo) return null;
    return (n / rFrom) * rTo;
  }, [rates, scopeCurrency]);

  /** Formats one row's amount in ITS organization's currency. */
  const money = useCallback((amount, rowOrCode, options) =>
    formatMoney(amount, currencyOf(rowOrCode) || scopeCurrency, options),
  [currencyOf, scopeCurrency]);

  /** Formats an already-consolidated amount in the scope currency. */
  const moneyScope = useCallback((amount, options) =>
    formatMoney(amount, scopeCurrency, options),
  [scopeCurrency]);

  /**
   * Sums rows across organizations, converting each into the scope currency.
   * → { value, complete, byCurrency, label }
   *   `complete: false` = at least one rate was missing; `value` then covers only
   *   what could be converted, and `label` gives the honest per-currency breakdown.
   */
  const sumScope = useCallback((rows, getAmount) => {
    const list = Array.isArray(rows) ? rows : [];
    const byCurrency = {};
    let value = 0;
    let complete = true;
    for (const row of list) {
      const raw = typeof getAmount === 'function' ? getAmount(row) : row;
      const n = typeof raw === 'string' ? parseFloat(raw) : Number(raw);
      if (!Number.isFinite(n)) continue;
      const cur = currencyOf(row) || scopeCurrency;
      byCurrency[cur] = (byCurrency[cur] || 0) + n;
    }
    for (const [cur, amount] of Object.entries(byCurrency)) {
      const converted = toScope(amount, cur);
      if (converted == null) complete = false;
      else value += converted;
    }
    const label = complete
      ? formatMoney(value, scopeCurrency)
      : Object.entries(byCurrency).map(([c, a]) => formatMoney(a, c)).join(' + ') || formatMoney(0, scopeCurrency);
    return { value, complete, byCurrency, label, currency: scopeCurrency };
  }, [currencyOf, scopeCurrency, toScope]);

  return {
    referenceCurrency: REFERENCE_CURRENCY,
    scopeCurrency,
    scopeCurrencies,
    isMultiCurrency,
    scopeSymbol: currencySymbol(scopeCurrency),
    currencyOf,
    symbolOf: (rowOrCode) => currencySymbol(currencyOf(rowOrCode) || scopeCurrency),
    toScope,
    money,
    moneyScope,
    sumScope,
    rates,
  };
}

export default useOrgCurrency;
